import React, { useState, useEffect } from "react";
import { agentService } from "../../services/agentService";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  TruckIcon,
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  PhoneIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { adminService } from "../../services/adminService";
import { bankService } from "../../services/bankService";
import LogoLoader from "../Common/LogoLoader";

const AgentDeliveryAdmin = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [filter, setFilter] = useState("all"); // all, today, week, month
  const [statusFilter, setStatusFilter] = useState("all"); // all, pending, in_progress, completed
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
  });
  const [bankDetails, setBankDetails] = useState(null);
  const [stats, setStats] = useState({
    totalDeliveries: 0,
    delivered: 0,
    inStore: 0,
    totalFee: 0,
    inStoreFee: 0,
    deliveredFee: 0,
    totalKg: 0
  });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 450);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Fetch bank details if selectedPaymentBank is just an ID
  const fetchBankDetails = async (bankId) => {
    try {
      console.log("Fetching bank details for ID:", bankId);
      const response = await bankService.getBankById(bankId);
      console.log("Bank service response:", response);
      return response.data;
    } catch (error) {
      console.error("Error fetching bank details:", error);
    }
    return null;
  };

  // Effect to fetch bank details when delivery is selected
  useEffect(() => {
    if (selectedDelivery?.selectedPaymentBank && typeof selectedDelivery.selectedPaymentBank === 'string') {
      console.log("Fetching bank details for ID:", selectedDelivery.selectedPaymentBank);
      fetchBankDetails(selectedDelivery.selectedPaymentBank).then(bank => {
        if (bank) {
          setBankDetails(bank);
          console.log("Bank details fetched:", bank);
        }
      });
    } else if (selectedDelivery?.selectedPaymentBank && typeof selectedDelivery.selectedPaymentBank === 'object') {
      setBankDetails(selectedDelivery.selectedPaymentBank);
    } else {
      setBankDetails(null);
    }
  }, [selectedDelivery]);


  useEffect(() => {
    fetchDeliveries();
  }, [
    currentPage,
    debouncedSearch,
    statusFilter,
    paymentStatusFilter,
    dateFilter,
    filter,
    customStartDate,
    customEndDate,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearch,
    statusFilter,
    paymentStatusFilter,
    dateFilter,
    filter,
    customStartDate,
    customEndDate,
  ]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchDeliveries = async () => {
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        paymentStatus: paymentStatusFilter !== 'all' ? paymentStatusFilter : undefined,
        date: filter !== 'custom' && dateFilter ? dateFilter : undefined,
        period: filter !== 'all' && filter !== 'custom' ? filter : undefined,
        ...(filter === 'custom' && customStartDate && customEndDate
          ? { startDate: customStartDate, endDate: customEndDate }
          : {}),
      };

      const data = await adminService.getAllDeliveries(params);
      setDeliveries(data.deliveries || []);
      setStats(data.stats || {});
      setPagination(
        data.pagination || {
          totalPages: 1,
          totalItems: (data.deliveries || []).length,
        }
      );
    } catch (error) {
      console.error("Error fetching deliveries:", error);
      setMessage("Error loading delivery data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "text-green-600 bg-green-100";
      case "instore":
        return "text-blue-600 bg-blue-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <CheckCircleIcon className="h-4 w-4" />;
      case "instore":
        return <TruckIcon className="h-4 w-4" />;
      case "pending":
        return <ClockIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  const totalPages = pagination.totalPages || 1;
  const totalItems = pagination.totalItems ?? deliveries.length;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex =
    totalItems === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalItems);
  const currentDeliveries = deliveries;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const totalDeliveries = stats.totalDeliveries || 0;
  const deliveredDeliveries = stats.delivered || 0;
  const inStoreDeliveries = stats.inStore || 0;
  const totalFee = stats.totalFee || 0;
  const inStoreFee = stats.inStoreFee || 0;
  const deliveredFee = stats.deliveredFee || 0;
  const totalKg = stats.totalKg || 0;

  const isDefaultPeriodView =
    filter === 'all' ||
    (filter === 'custom' && (!customStartDate || !customEndDate));

  const handleStatusUpdate = async (delivery_id, newStatus) => {
    try {
      setLoading(true);
      await adminService.updateDeliveryStatus(delivery_id, newStatus);
      setNotification({ type: 'success', message: 'Delivery status updated successfully!' });
      setSelectedDelivery(null);
      fetchDeliveries();
    } catch (err) {
      console.error("Error updating status:", err);
      setNotification({ type: 'error', message: 'Failed to update status.' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDeliveryStatus = async (delivery_id, newStatus) => {
    try {
      setLoading(true);
      await adminService.updateDeliveryStatus(delivery_id, newStatus);
      setNotification({ show: true, type: 'success', message: 'Delivery status updated successfully!' });
      // Update the selected delivery in state
      setSelectedDelivery(prev => ({
        ...prev,
        pickupStatus: newStatus
      }));
      // Refresh the deliveries list
      await fetchDeliveries();
    } catch (error) {
      console.error("Error updating delivery status:", error);
      setNotification({ show: true, type: 'error', message: 'Error updating delivery status' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentStatus = async (delivery_id, newStatus) => {
    try {
      setLoading(true);
      await adminService.updatePaymentStatus(delivery_id, newStatus);
      setNotification({ show: true, type: 'success', message: 'Payment status updated successfully!' });
      // Update the selected delivery in state
      setSelectedDelivery(prev => ({
        ...prev,
        agentPaymentStatus: newStatus
      }));
      // Refresh the deliveries list
      await fetchDeliveries();
    } catch (error) {
      console.error("Error updating payment status:", error);
      setNotification({ show: true, type: 'error', message: 'Error updating payment status' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Delivery Management
        </h1>
        <p className="text-gray-600">
          Monitor all delivery operations across the platform.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className="bg-red-100 text-red-700 border border-red-200 p-4 rounded-md">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            {message}
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex items-center">
            <div className="ml-3">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 min-[480px]:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-blue-100 rounded-lg shrink-0">
              <TruckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                Total Deliveries
              </p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                {totalDeliveries}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-green-100 rounded-lg shrink-0">
              <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Delivered</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                {deliveredDeliveries}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-yellow-100 rounded-lg shrink-0">
              <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">In Store</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                {inStoreDeliveries}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-purple-100 rounded-lg shrink-0">
              <TruckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                Total Fee
              </p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                ₦{totalFee.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-yellow-100 rounded-lg shrink-0">
              <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">In Store Fee</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                ₦{inStoreFee.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-green-100 rounded-lg shrink-0">
              <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Delivered Fee</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                ₦{deliveredFee.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Delivery History ({totalItems})
          </h2>
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
            <select
              value={filter}
              onChange={(e) => {
                const v = e.target.value;
                setFilter(v);
                if (v !== 'custom') {
                  setCustomStartDate('');
                  setCustomEndDate('');
                }
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom range</option>
            </select>
            {filter === 'custom' && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">From</span>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <span className="text-sm text-gray-600 whitespace-nowrap">To</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate || undefined}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Deliveries List */}
      {currentDeliveries.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <TruckIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No deliveries found
          </h3>
          <p className="text-gray-600">
            {isDefaultPeriodView && statusFilter === "all"
              ? "No deliveries have been made yet."
              : "No deliveries match the selected filters."}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Kg
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentDeliveries.map((delivery) => (
                  <tr key={delivery._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{delivery._id?.slice(-6) || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {delivery.agent?.firstName || "N/A"}{" "}
                        {delivery.agent?.lastName || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {delivery.totalQuantity || 0} kg
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₦{delivery.totalProfit?.toLocaleString() || "0"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          delivery.pickupStatus
                        )}`}
                      >
                        {getStatusIcon(delivery.pickupStatus)}
                        <span className="ml-1 capitalize">
                          {delivery.pickupStatus?.replace("_", " ")}
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          delivery.agentPaymentStatus === 'paid' 
                            ? 'text-green-600 bg-green-100' 
                            : 'text-yellow-600 bg-yellow-100'
                        }`}
                      >
                        <span className="ml-1 capitalize">
                          {delivery.agentPaymentStatus || "processing"}
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(delivery.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedDelivery(delivery)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delivery Details Modal */}
      {selectedDelivery && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Delivery Details #{selectedDelivery._id?.slice(-10)}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      const printContent = document.getElementById('delivery-modal-content');
                      const originalContent = document.body.innerHTML;
                      document.body.innerHTML = printContent.innerHTML;
                      window.print();
                      document.body.innerHTML = originalContent;
                      window.location.reload();
                    }}
                    className="px-3 py-1 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Print
                  </button>
                  <button
                    onClick={() => setSelectedDelivery(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div id="delivery-modal-content" className="space-y-6">
                {/* Agent Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Agent Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Agent Name
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedDelivery.agent?.firstName || "N/A"}{" "}
                        {selectedDelivery.agent?.lastName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Agent Phone
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedDelivery.agent?.phoneNumber || "N/A"}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Agent Address
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedDelivery.agent?.homeAddress || selectedDelivery.agent?.agentDetails?.localGovernmentAddress || selectedDelivery.agent?.agentDetails?.streetAddress || "N/A"}
                      </p>
                    </div>
                  </div>
                  
                  {/* Payment Bank Details within Agent Information */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h5 className="text-md font-medium text-gray-800 mb-3">Payment Bank Details</h5>
                    
                    {bankDetails ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Bank Name
                          </label>
                          <p className="text-sm text-gray-900 mt-1">
                            {bankDetails.bankName || "N/A"}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Account Number
                          </label>
                          <p className="text-sm text-gray-900 mt-1">
                            {bankDetails.accountNumber || "N/A"}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Account Name
                          </label>
                          <p className="text-sm text-gray-900 mt-1">
                            {bankDetails.accountName || "N/A"}
                          </p>
                        </div>
                      </div>
                    ) : selectedDelivery?.selectedPaymentBank && typeof selectedDelivery.selectedPaymentBank === 'string' ? (
                      <div className="text-center py-4">
                        <LogoLoader label="Loading bank details" />
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500 italic">No payment bank selected for this delivery</p>
                        <p className="text-xs text-gray-400 mt-1">Agent needs to select a payment bank before delivering products</p>
                      </div>
                    )}
                  </div>
                </div>


                {/* Status Information */}
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Status Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Delivery Status
                      </label>
                      <div className="mb-2">
                        <span className="text-sm text-gray-600">
                          Current: <span className="font-medium">{selectedDelivery.pickupStatus || "In-Store"}</span>
                        </span>
                      </div>
                      <select
                        value={selectedDelivery.pickupStatus || "In-Store"}
                        onChange={(e) => handleUpdateDeliveryStatus(selectedDelivery._id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="In-Store">In-Store</option>
                        <option value="Delivered">Delivered</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Status
                      </label>
                      <div className="mb-2">
                        <span className="text-sm text-gray-600">
                          Current: <span className="font-medium">{selectedDelivery.agentPaymentStatus || "processing"}</span>
                        </span>
                      </div>
                      <select
                        value={selectedDelivery.agentPaymentStatus || "processing"}
                        onChange={(e) => handleUpdatePaymentStatus(selectedDelivery._id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="processing">Processing</option>
                        <option value="paid">Paid</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Materials List */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Materials Delivered</h4>
                  <div className="space-y-3">
                    {selectedDelivery.materials && selectedDelivery.materials.length > 0 ? (
                      selectedDelivery.materials.map((material, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-white rounded border">
                          <div>
                            <p className="font-medium text-gray-900">{material.material?.name || "Unknown Material"}</p>
                            <p className="text-sm text-gray-600">₦{material.material?.pricePerKg || 0}/kg</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{material.quantity} kg</p>
                            <p className="text-sm text-gray-600">
                              ₦{material.profit?.toLocaleString() || "0"}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">No materials found</p>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-100 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600">Total Weight</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedDelivery.totalQuantity || 0} kg
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600">Total Fee</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ₦{selectedDelivery.totalProfit?.toLocaleString() || "0"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setSelectedDelivery(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && totalItems > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing                 <span className="font-medium">{startIndex}</span> to{' '}
                <span className="font-medium">{endIndex}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-green-50 border-green-500 text-green-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDeliveryAdmin;
